import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInvoicesByIndex } from "../../hooks/useInvoiceByIndex";
import { MdReceipt, MdChevronLeft, MdChevronRight } from "react-icons/md";

const InvoiceTable = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const { data, isLoading, error } = useInvoicesByIndex(index);

  const handlePrevious = () => {
    if (data?.hasPrevious) {
      setIndex(index - 1);
    }
  };

  const handleNext = () => {
    if (data?.hasNext) {
      setIndex(index + 1);
    }
  };

  const getMonthNumber = (monthName) => {
    const months = {
      "JANUARY": 1,
      "FEBRUARY": 2,
      "MARCH": 3,
      "APRIL": 4,
      "MAY": 5,
      "JUNE": 6,
      "JULY": 7,
      "AUGUST": 8,
      "SEPTEMBER": 9,
      "OCTOBER": 10,
      "NOVEMBER": 11,
      "DECEMBER": 12
    };
    return months[monthName] || 0;
  };

  const handleRowClick = (invoice) => {
    const [monthName, year] = invoice.monthYear.split(' ');
    const monthNumber = getMonthNumber(monthName);
    
    navigate(`/invoiceDetail/${invoice.invoiceId}`, {
      state: {
        invoiceId: invoice.invoiceId,
        month: monthNumber,
        year: parseInt(year),
        amount: invoice.totalAmount,
        status: invoice.status,
        monthYear: invoice.monthYear
      }
    });
  };

  if (isLoading) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4">
        <div className="text-center py-8">
          <p className="text-red-400">Error loading invoices: {error.message}</p>
        </div>
      </div>
    );
  }

  const invoices = data?.invoices || [];
  const hasNext = data?.hasNext || false;
  const hasPrevious = data?.hasPrevious || false;

  const getStatusBadge = (status) => {
    const statusColors = {
      Paid: "bg-green-500/20 text-green-400 border-green-500",
      Pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500",
      Failed: "bg-red-500/20 text-red-400 border-red-500",
      Unpaid: "bg-orange-500/20 text-orange-400 border-orange-500",
    };
    const color = statusColors[status] || "bg-gray-500/20 text-gray-400 border-gray-500";
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${color}`}>
        {status}
      </span>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
      <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
        <MdReceipt className="text-primary-cyan text-2xl" />
        <h3 className="text-xl font-semibold text-white">Invoices</h3>
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <div className="min-w-[500px] sm:min-w-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Invoice ID</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Month/Year</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Amount</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr 
                  key={invoice.invoiceId} 
                  onClick={() => handleRowClick(invoice)}
                  className="border-b border-gray-700/50 hover:bg-white/5 transition cursor-pointer"
                >
                  <td className="py-3 px-2 sm:px-3">
                    <span className="text-white font-mono text-[10px] sm:text-xs break-all">
                      {invoice.invoiceId}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    <span className="text-gray-300 text-xs sm:text-sm">
                      {invoice.monthYear}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    <span className="text-white font-medium text-xs sm:text-sm">
                      {formatAmount(invoice.totalAmount)}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    {getStatusBadge(invoice.status)}
                   </td>
                 </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {invoices.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-400">No invoices found</p>
        </div>
      )}

      {invoices.length > 0 && (
        <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 pt-4 border-t border-gray-700">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition text-sm sm:text-base
              ${hasPrevious 
                ? "bg-primary-cyan hover:bg-primary-cyan/80 text-white" 
                : "bg-gray-700 text-gray-400 cursor-not-allowed"}
            `}
          >
            <MdChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>
          
          <span className="text-gray-400 text-xs sm:text-sm">
            Page {index + 1}
          </span>
          
          <button
            onClick={handleNext}
            disabled={!hasNext}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition text-sm sm:text-base
              ${hasNext 
                ? "bg-primary-cyan hover:bg-primary-cyan/80 text-white" 
                : "bg-gray-700 text-gray-400 cursor-not-allowed"}
            `}
          >
            <span className="hidden sm:inline">Next</span>
            <MdChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceTable;